/**
 * Zod schemas for the portable (v1) selection-snapshot storage format.
 * See ./index.ts for the feature-level overview and design rationale.
 *
 * Key design:
 * - Categorical filters store category NAMES (strings) instead of numeric IDs
 * - This makes snapshots portable across different data loads
 * - Numeric ID conversion happens during deserialization in the context of target data
 */

import { z } from 'zod';
import { TpEnSelectionFilterTypes } from '../interfaces';

/**
 * Fast O(1) ID array validator.
 * Checks that the payload is an Array and that the first element is a string,
 * avoiding element-by-element loops over 1M+ items.
 */
export const fastStringArraySchema = z.custom<string[]>(
  (val) => Array.isArray(val) && (val.length === 0 || typeof val[0] === 'string'),
  { message: 'Expected an array of string IDs' },
);

// ============================================================================
// Individual step schemas (all 8 filter types)
// ============================================================================

export const categoricalStepSchema = z.object({
  type: z.literal(TpEnSelectionFilterTypes.CATEGORICAL),
  key: z.string(),
  dataTableId: z.string(),
  colId: z.string(),
  selectedCategoryIdList: z.array(z.string()),
  excludedCategoryIdList: z.array(z.string()),
});

export const dualCategoricalStepSchema = z.object({
  type: z.literal(TpEnSelectionFilterTypes.DUALCATEGORICAL),
  key: z.string(),
  dataTableId: z.string(),
  colId1: z.string(),
  colId2: z.string(),
  selectedCellsList: z.array(
    z.object({
      val1: z.string(),
      val2: z.string(),
    }),
  ),
});

export const intersectionsStepSchema = z.object({
  type: z.literal(TpEnSelectionFilterTypes.INTERSECTIONS),
  key: z.string(),
  dataTableId: z.string(),
  colId: z.string(),
  intersections: z.array(z.array(z.string())), // 2D array of category names
});

export const multiLevelCatStepSchema = z.object({
  type: z.literal(TpEnSelectionFilterTypes.MULTILEVELCAT),
  key: z.string(),
  dataTableId: z.string(),
  colIds: z.array(z.string()),
  selectedElements: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      selectedItemIds: fastStringArraySchema,
    }),
  ),
});

export const numericalRangeStepSchema = z.object({
  type: z.literal(TpEnSelectionFilterTypes.NUMERICALRANGE),
  key: z.string(),
  dataTableId: z.string(),
  colId: z.string(),
  rangeMin: z.number(),
  rangeMax: z.number(),
});

export const lasso2DStepSchema = z.object({
  type: z.literal(TpEnSelectionFilterTypes.LASSO2D),
  key: z.string(),
  dataTableId: z.string(),
  colIdX: z.string(),
  colIdY: z.string(),
  lassoPoints: z.array(
    z.object({
      x: z.number(),
      y: z.number(),
    }),
  ),
  selectedItemIds: fastStringArraySchema,
});

export const manualStepSchema = z.object({
  type: z.literal(TpEnSelectionFilterTypes.MANUAL),
  key: z.string(),
  description: z.string(),
  selectedItemIds: fastStringArraySchema,
  stepIdentifier: z.string().optional(),
});

export const importStepSchema = z.object({
  type: z.literal(TpEnSelectionFilterTypes.IMPORT),
  description: z.string(),
  key: z.string(),
  assetId: z.string(),
  assetName: z.string().optional(),
  selectedItemIds: fastStringArraySchema,
});

// Union of all step schemas
export const selectionFilterStepSchema = z.union([
  categoricalStepSchema,
  dualCategoricalStepSchema,
  intersectionsStepSchema,
  multiLevelCatStepSchema,
  numericalRangeStepSchema,
  lasso2DStepSchema,
  manualStepSchema,
  importStepSchema,
]);

// ============================================================================
// Root snapshot schema
// ============================================================================

export const filterContainerSchema = z.object({
  steps: z.array(selectionFilterStepSchema),
});

export const canonicalTypeSelectionSchema = z.object({
  selectionFilter: filterContainerSchema,
  manualToggleIds: fastStringArraySchema,
  restrictionFilter: filterContainerSchema.nullable(),
  restrictionManualToggleIds: fastStringArraySchema,
  canonicalTypeName: z.string(),
});

export const frozenSelectionSnapshotSchema = canonicalTypeSelectionSchema;

// Root Storage Payload Schema
export const selectionStoragePayloadV1Schema = z.object({
  version: z.literal(1),
  sourceAssetId: z.string(),
  sourceAssetName: z.string(),
  assetType: z.string(),
  canonicalTypeSelections: z.record(z.string(), canonicalTypeSelectionSchema),
});

// Infer TS Type directly from Schema (No duplicate interface definitions needed!)
export type TpSelectionStoragePayloadV1 = z.infer<typeof selectionStoragePayloadV1Schema>;
export type TpCanonicalTypeSelection = z.infer<typeof canonicalTypeSelectionSchema>;
export type TpFrozenSelectionSnapshot = z.infer<typeof frozenSelectionSnapshotSchema>;
export type TpFilterContainer = z.infer<typeof filterContainerSchema>;
export type TpSelectionFilterStep = z.infer<typeof selectionFilterStepSchema>;

// All individual step types
export type TpCategoricalStepSchema = z.infer<typeof categoricalStepSchema>;
export type TpDualCategoricalStepSchema = z.infer<typeof dualCategoricalStepSchema>;
export type TpIntersectionsStepSchema = z.infer<typeof intersectionsStepSchema>;
export type TpMultiLevelCatStepSchema = z.infer<typeof multiLevelCatStepSchema>;
export type TpNumericalRangeStepSchema = z.infer<typeof numericalRangeStepSchema>;
export type TpLasso2DStepSchema = z.infer<typeof lasso2DStepSchema>;
export type TpManualStepSchema = z.infer<typeof manualStepSchema>;
export type TpImportStepSchema = z.infer<typeof importStepSchema>;
